import _defineProperty from "@babel/runtime/helpers/defineProperty";
import MuiDialog from "@material-ui/core/Dialog";
import MuiDialogActions from "@material-ui/core/DialogActions";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogContentText from "@material-ui/core/DialogContentText";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import { withStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import classNames from "classnames";
import PropTypes from "prop-types";
import deprecated from "prop-types-extra/lib/deprecated";
import React, { Component } from "react";
import Button from "../Button";
import ButtonGroup from "../ButtonGroup";
import Typography from "../Typography";
import { promiseViaComponent } from "../Utilities/ReactHelper";
import { styles } from "./styles";
class Dialog extends Component {
  constructor(props) {
    super(props);
    // This fires when the dialog opens. We want to wait for it to open to find its height.
    _defineProperty(this, "handleEntered", () => {
      // Trigger the onEntered function passed as a prop
      if (typeof this.props.onEntered === "function") this.props.onEntered();
      if (this.props.fullHeight) this.calculateHeight();
    });
    _defineProperty(this, "calculateHeight", () => {
      const titleHeight = this.titleRef.current ? this.titleRef.current.offsetHeight : 0;
      const actionsHeight = this.actionsRef.current ? this.actionsRef.current.offsetHeight : 0;

      // These values come from styles, if they are changed update here too
      const newMinHeight = "calc(100vh - 96px - 24px - " + titleHeight + "px - " + actionsHeight + "px";
      if (this.state.minHeight !== newMinHeight) {
        this.setState({
          transitioningHeight: true
        });
        this.setState({
          minHeight: newMinHeight
        });
        this.timeoutObject = setTimeout(() => {
          this.setState({
            transitioningHeight: false
          });
        }, 500);
      }
    });
    _defineProperty(this, "componentDidUpdate", () => {
      const {
        open,
        fullHeight
      } = this.props;
      if (open && fullHeight) {
        this.calculateHeight();
      }
    });
    _defineProperty(this, "componentWillUnmount", () => {
      clearTimeout(this.timeoutObject);
    });
    _defineProperty(this, "render", () => {
      const {
        actions,
        content,
        children,
        id,
        fullWidth,
        fullHeight,
        maxWidth,
        onClose,
        onEnter,
        onEntering,
        onExit,
        onExited,
        onExiting,
        open,
        title,
        className,
        classes,
        disableBackdropClick,
        BackdropProps,
        onBackdropClick,
        disableClose
      } = this.props;
      return /*#__PURE__*/React.createElement(MuiDialog, {
        id: id,
        open: open,
        className: classNames("prmbl-dialog", className, classes.root),
        fullWidth: fullWidth,
        maxWidth: maxWidth,
        onBackdropClick: onBackdropClick,
        onClose: (event, reason) => {
          if (disableBackdropClick && reason === "backdropClick") {
            return;
          }
          if (onClose) onClose(event, reason);
        },
        TransitionProps: {
          onEnter: onEnter,
          onEntered: this.handleEntered,
          onEntering: onEntering,
          onExit: onExit,
          onExited: onExited,
          onExiting: onExiting,
          role: "dialog",
          "aria-labelledby": id ? `${id}-title` : undefined,
          "aria-label": id ? undefined : title
        },
        classes: {
          paper: classes.dialog
        },
        disableBackdropClick: disableBackdropClick,
        BackdropProps: BackdropProps,
        role: "none" // The transition holds the role for the dialog
      }, /*#__PURE__*/React.createElement("div", {
        ref: this.titleRef
      }, /*#__PURE__*/React.createElement(MuiDialogTitle, {
        id: id ? `${id}-title` : undefined,
        className: classNames("prmbl-dialog-title", classes.title),
        disableTypography: true
      }, /*#__PURE__*/React.createElement(Typography, {
        component: "h1",
        variant: "h6"
      }, title))), /*#__PURE__*/React.createElement(Button, {
        type: "icon",
        size: "small",
        disabled: disableClose,
        onClick: onClose,
        className: classNames("prmbl-dialog-close", classes.closeButton),
        title: "Close this Dialog"
      }, /*#__PURE__*/React.createElement(CloseIcon, null)), /*#__PURE__*/React.createElement(MuiDialogContent, {
        className: classNames("prmbl-dialog-content", classes.dialogContent, {
          [classes.dialogContentMaxHeightTransition]: fullHeight && this.state.transitioningHeight
        }),
        style: fullHeight ? {
          minHeight: this.state.minHeight
        } : undefined
      }, typeof children === "string" || typeof content === "string" ? /*#__PURE__*/React.createElement(MuiDialogContentText, null, children || content) : /*#__PURE__*/React.createElement(React.Fragment, null, children || content)), actions && /*#__PURE__*/React.createElement("div", {
        ref: this.actionsRef
      }, /*#__PURE__*/React.createElement(MuiDialogActions, {
        className: classNames("prmbl-dialog-actions", classes.buttons)
      }, /*#__PURE__*/React.createElement(ButtonGroup, {
        layout: "right"
      }, actions))));
    });
    this.state = {
      minHeight: "",
      transitioningHeight: true
    };
    this.titleRef = /*#__PURE__*/React.createRef();
    this.actionsRef = /*#__PURE__*/React.createRef();
    this.timeoutObject = null;
  }
}
_defineProperty(Dialog, "propTypes", {
  /**
   * The actions for the dialog. Include JSX of buttons. These will already be wrapped in a ButtonGroup for modal purposes.
   *
   * The primary action should use color="primary", be the first item, and should be descriptive.
   */
  actions: PropTypes.node,
  /**
   * The html id
   */
  id: PropTypes.string,
  /**
   * @deprecated
   * Use a child node instead
   */
  content: deprecated(PropTypes.node, "This Dialog uses the deprecated `content` prop. Use a child node instead."),
  /**
   * The dialog content. Explain the situation, and prompt the user to choose a relevant action
   */
  children: PropTypes.node,
  /**
   * If `true`, the dialog stretches to `maxWidth`.
   */
  fullWidth: PropTypes.bool,
  /**
   * If `true`, the dialog stretches to the viewport height.
   */
  fullHeight: PropTypes.bool,
  /**
   * Determine the max width of the dialog. The dialog width grows with the size of the screen,
   * this property is useful on the desktop where you might need some coherent different width size across your application.
   * Set to `false` to disable `maxWidth`.
   */
  maxWidth: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", false]),
  /**
   *Callback fired when the component requests to be closed.
   *
   ***Signature**: `function(event: object) => void`
   *
   * *event*: The event source of the callback
   */
  onClose: PropTypes.func.isRequired,
  /**
   *Callback fired before the dialog enters.
   */
  onEnter: PropTypes.func,
  /**
   *Callback fired when the dialog has entered.
   */
  onEntered: PropTypes.func,
  /**
   *	Callback fired when the dialog is entering.
   */
  onEntering: PropTypes.func,
  /**
   *Callback fired before the dialog exits.
   */
  onExit: PropTypes.func,
  /**
   *Callback fired when the dialog has exited.
   */
  onExited: PropTypes.func,
  /**
   *Callback fired when the dialog is exiting.
   */
  onExiting: PropTypes.func,
  /**
   *If `true`, the Dialog is open.
   */
  open: PropTypes.bool.isRequired,
  /**
   * The title of the dialog. This should be short, self-describing, and title case. Avoid questions when possible.
   */
  title: PropTypes.string.isRequired,
  /**
   * Classes to apply to the root
   */
  className: PropTypes.string,
  /**
   * @ignore
   *
   * Utilizing this to style up the page don't want to expose this in the style guide.
   * Auto-populated by material-ui withStyles()
   */
  classes: PropTypes.object.isRequired,
  /**
   * If true, backdrop will not call the onClose function.
   */
  disableBackdropClick: PropTypes.bool,
  /**
   * Props applied to the Backdrop element.
   */
  BackdropProps: PropTypes.object,
  /**
   * @deprecated
   * Function to be called after the backdrop is clicked. Will call before the onClose function is called.
   */
  onBackdropClick: deprecated(PropTypes.func, "Use onClose with reason === backdropClick"),
  /**
   * If true, the close button will be disabled
   */
  disableClose: PropTypes.bool
});
_defineProperty(Dialog, "defaultProps", {
  fullWidth: false,
  fullHeight: false,
  maxWidth: "sm"
});
const DialogStyled = withStyles(styles)(Dialog);
export { Dialog };
export const dialogConfirm = message => {
  return promiseViaComponent((close, resolve, reject, id) => /*#__PURE__*/React.createElement(DialogStyled, {
    actions: [/*#__PURE__*/React.createElement(Button, {
      id: id + "-button-confirm",
      key: id + "-button-confirm",
      onClick: () => {
        resolve(true);
      },
      color: "primary"
    }, "OK"), /*#__PURE__*/React.createElement(Button, {
      id: id + "-button-cancel",
      key: id + "-button-cancel",
      onClick: () => {
        resolve(false);
      }
    }, "Cancel")],
    id: id + "-confirmation",
    onClose: () => {
      resolve(false);
    },
    open: true,
    title: "Confirmation"
  }, message));
};
export default DialogStyled;